import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';

import FieldDragTypes from '../../constants/FieldDragTypes';
import {
  getValidAdjustedPageDropPosition,
  getDefaultWidthAndHeightForField,
  recalculatePositionCoordinatesForField
} from '../../lib/documentHelper';

const FieldCreationDropLayer = ({
  children,
  onDrop,
  pageId,
  pageWidth,
  pageColumns,
  pageRowHeight,

  /**
   * Mock monitor for testing. I couldn't figure out any
   * reasonable way to mock the monitor in order to
   * get a client offset value that is valid and testable.
   * Without the mock the client offset always returns
   * x and y values as undefined.
   */
  mockTestDndMonitor, //Only for testing
  mockRef = null, //Only for testing
}) => {

  /**
   * IMPORTANT NOTE on scrollTop not being needed for field y position.
   *
   * The dnd module gives us the drop position relative to the top of the dropzone
   * element, not relative to the viewport.
   *
   * So if the dropzone container surrounding the form increases in height to
   * constantly contain the entire form then the dropzone will give us the
   * proper y position based on the height of the full dropzone and our drop
   * position.
   *
   * When do you need to use scrollTop with the drop position coordinates? You need to
   * do this when you don't allow the dropzone to grow to the height of the entire form.
   */

  const [ref, setRef] = useState(mockRef);

  const [collectedProps, drop] = useDrop({
    drop: (item, monitor) => {

      const dndMonitor = mockTestDndMonitor || monitor;

      /**
       * Step 1: Adjust drop location to top left corner of
       * drag preview image instead of mouse position.
       *
       * Adjust x and y coordinates so that the drop position
       * matches the drag preview image instead of the mouse location.
       *
       */
      const click = dndMonitor.getInitialClientOffset();
      const source = dndMonitor.getInitialSourceClientOffset();

      const xDifference = click.x - source.x;
      const yDifference = click.y - source.y;

      const clientOffset = dndMonitor.getClientOffset();

      const targetX = clientOffset.x - xDifference;
      const targetY = clientOffset.y - yDifference;

      let data = { ...item, x: targetX, y: targetY };

      if (ref) {

        /**
         * Step 2: Get proper position properties (X and Y) for drop position within the bounds of the page
         */
        data = getValidAdjustedPageDropPosition(data, ref.getBoundingClientRect());

        /**
         * Step 3: Get default width and height for field type
         */
        data = getDefaultWidthAndHeightForField(data);

        /**
         * Step 4: Adjust the x and y properties from exact pixel positions from step 1
         * to grid columns and row heights position.
         */
        data = {
          ...data,
          ...recalculatePositionCoordinatesForField(data, pageColumns, pageRowHeight, pageWidth, 1)
        };

      }

      if (onDrop) onDrop(pageId, data);

    },
    collect: (monitor) => ({
      /**
       * <--------------NOTICE-------------->
       * We are converting the boolean value to a string here to fix the following warning:
       * Received `false` for a non-boolean attribute `hovered`.
       */
      hovered: monitor.isOver().toString()
    }),
    accept: FieldDragTypes.all
  });

  return (
    <div
      data-testid="field-creation-drop-layer"
      style={{
        display: 'flex',
        flex: 1
      }}
      ref={(e) => {
        if (e) {
          drop(e);
          if (!ref) setRef(e);
        }
      }}
      {...collectedProps}
    >
      {children}
    </div>
  );

};

FieldCreationDropLayer.propTypes = {
  onDrop: PropTypes.func,
  mockTestMonitor: PropTypes.object //Only for tests
};

export default FieldCreationDropLayer;
